import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { SPECIALTY_IDS, getSpecialty } from 'config/specialtyConfig';
import {
    hasPaidAccess,
    getPlansForSpecialty,
    ADDON_DISCOUNT_PERCENT,
    getAddonPrice,
    getSpecialtyFields,
    isTrialValid,
    getEffectivePrice,
} from 'config/subscriptionPlans';
import QuickBuyModal from './QuickBuyModal';

const AddOnSpecialtyBanks = ({ userDetails, activeSpecialty, paymentMethods, defaultCard }) => {
    const history = useHistory();
    const [selectedPlanIndex, setSelectedPlanIndex] = useState({});
    const [quickBuyState, setQuickBuyState] = useState(null);

    if (!userDetails) return null;

    const fields = getSpecialtyFields(activeSpecialty);
    const hasEverPaid = hasPaidAccess(userDetails, activeSpecialty) || !!userDetails[fields.subscriptionEndDate];
    if (!hasEverPaid) return null;

    const trialActive = isTrialValid(userDetails[fields.trialStartDate]);

    const addonSpecialties = SPECIALTY_IDS.filter(id => {
        if (id === activeSpecialty) return false;
        if (hasPaidAccess(userDetails, id)) return false;
        return true;
    });

    if (addonSpecialties.length === 0) return null;

    const handlePurchase = (specId) => {
        const plans = getPlansForSpecialty(specId);
        const idx = selectedPlanIndex[specId] ?? 3;
        const plan = plans[idx];
        const price = getAddonPrice(plan, trialActive);

        if (defaultCard) {
            setQuickBuyState({ plan, price, specId });
        } else {
            history.push({
                pathname: '/client/payment',
                state: { selectedPlan: plan.priceId, specialty: specId, isAddon: true },
            });
        }
    };

    const colorMap = {
        danger: { bg: '#f5365c', bgLight: 'rgba(245,54,92,0.08)', border: '#f5365c' },
        info: { bg: '#11cdef', bgLight: 'rgba(17,205,239,0.08)', border: '#11cdef' },
        primary: { bg: '#5e72e4', bgLight: 'rgba(94,114,228,0.08)', border: '#5e72e4' },
    };

    return (
        <>
            <Row className="mt-3">
                <Col lg="12">
                    {addonSpecialties.map(specId => {
                        const spec = getSpecialty(specId);
                        const plans = getPlansForSpecialty(specId);
                        const idx = selectedPlanIndex[specId] ?? 3;
                        const plan = plans[idx];
                        const addonPrice = getAddonPrice(plan);
                        const originalPrice = getEffectivePrice(plan);
                        const colors = colorMap[spec.color] || colorMap.primary;

                        return (
                            <Card
                                key={specId}
                                className="border-0 shadow mb-3"
                                style={{ borderLeft: `4px solid ${colors.border}`, overflow: 'hidden' }}
                            >
                                <CardBody className="py-3 px-4">
                                    <div className="d-flex align-items-center flex-wrap">
                                        {/* Icon */}
                                        <div
                                            className="d-flex align-items-center justify-content-center mr-3"
                                            style={{
                                                width: '48px', height: '48px', borderRadius: '50%',
                                                backgroundColor: colors.bgLight, flexShrink: 0,
                                            }}
                                        >
                                            <i className={spec.icon} style={{ color: colors.bg, fontSize: '1.25rem' }} />
                                        </div>

                                        {/* Name + discount badge */}
                                        <div className="mr-3" style={{ minWidth: 0 }}>
                                            <h4 className="mb-0 text-dark d-flex align-items-center flex-wrap">
                                                {spec.name}
                                                <span
                                                    className="ml-2"
                                                    style={{
                                                        fontSize: '0.7rem',
                                                        padding: '3px 10px',
                                                        backgroundColor: '#2dce89',
                                                        color: '#fff',
                                                        borderRadius: '50px',
                                                        fontWeight: '700',
                                                    }}
                                                >
                                                    SAVE {ADDON_DISCOUNT_PERCENT}%
                                                </span>
                                            </h4>
                                            <p className="text-muted mb-0 small">Add-on discount available</p>
                                        </div>

                                        {/* Plan dropdown */}
                                        <div className="mr-3 mt-2 mt-md-0">
                                            <select
                                                className="form-control form-control-sm"
                                                style={{ minWidth: '200px' }}
                                                value={idx}
                                                onChange={e => setSelectedPlanIndex(prev => ({ ...prev, [specId]: parseInt(e.target.value) }))}
                                            >
                                                {plans.map((p, i) => (
                                                    <option key={p.id} value={i}>
                                                        {p.months} mo — ${getAddonPrice(p, trialActive).toFixed(2)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Price */}
                                        <div className="mr-3 text-center mt-2 mt-md-0" style={{ minWidth: '100px' }}>
                                            <span className="h2 mb-0 text-dark font-weight-bold">${addonPrice.toFixed(2)}</span>
                                            <span className="text-muted ml-1 small" style={{ textDecoration: 'line-through' }}>${originalPrice}</span>
                                        </div>

                                        {/* CTA */}
                                        <button
                                            className="btn btn-sm mt-2 mt-md-0 ml-md-auto font-weight-bold"
                                            style={{ backgroundColor: colors.bg, color: '#fff', whiteSpace: 'nowrap' }}
                                            onClick={() => handlePurchase(specId)}
                                        >
                                            <i className="fas fa-plus-circle mr-1" />
                                            Add {spec.name}
                                        </button>
                                    </div>
                                </CardBody>
                            </Card>
                        );
                    })}
                </Col>
            </Row>

            {quickBuyState && (
                <QuickBuyModal
                    isOpen={!!quickBuyState}
                    toggle={() => setQuickBuyState(null)}
                    plan={quickBuyState.plan}
                    defaultCard={defaultCard}
                    userDetails={userDetails}
                    activeSpecialty={activeSpecialty}
                    isAddon={true}
                    addonPrice={quickBuyState.price}
                    targetSpecialty={quickBuyState.specId}
                    onUseDifferentCard={() => {
                        const { plan, specId } = quickBuyState;
                        setQuickBuyState(null);
                        history.push({
                            pathname: '/client/payment',
                            state: { selectedPlan: plan.priceId, specialty: specId, isAddon: true },
                        });
                    }}
                />
            )}
        </>
    );
};

const mapStateToProps = state => ({
    userDetails: state.loginAuth.userDetails,
    activeSpecialty: state.specialty.activeSpecialty,
    paymentMethods: state.paymentMethods?.paymentMethods || [],
    defaultCard: (state.paymentMethods?.paymentMethods || []).find(pm => pm.isDefault) || null,
});

export default connect(mapStateToProps)(AddOnSpecialtyBanks);
